import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { fetchWithDemoFallback } from '../../../helpers/apiHelper';
import ASTITitle from '../../labels/ASTITitle';
import ASTIText from '../../labels/ASTIText';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const UNASSIGNED_DIVISION = 'Unassigned Division';
const LOOKBACK_30_DAYS = '30_days';
const LOOKBACK_LAST_MONTH = 'last_month';
const LOOKBACK_LAST_QUARTER = 'last_quarter';
const LOOKBACK_YTD = 'ytd';
const RANK_COLUMN_WIDTH = 56;
const LABEL_COLUMN_WIDTH = 320;
const AMOUNT_COLUMN_WIDTH = 170;
const NET_COLUMN_WIDTH = 190;
const GROUP_INDENT_LEVEL = 5;
const CATEGORY_INDENT_LEVEL = 10;

function parseTransactionDate(value) {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const parsed = new Date(`${raw}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [dd, mm, yyyy] = raw.split('/');
    const parsed = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (/^\d{8}$/.test(raw)) {
    const yyyy = raw.slice(0, 4);
    const mm = raw.slice(4, 6);
    const dd = raw.slice(6, 8);
    const parsed = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function buildLookbackBounds(lookback, referenceDate) {
  const end = new Date(referenceDate);
  end.setHours(23, 59, 59, 999);

  if (lookback === LOOKBACK_LAST_MONTH) {
    const start = new Date(end.getFullYear(), end.getMonth() - 1, 1);
    start.setHours(0, 0, 0, 0);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 0);
    endMonth.setHours(23, 59, 59, 999);
    return { start, end: endMonth };
  }

  if (lookback === LOOKBACK_LAST_QUARTER) {
    const currentQuarterStartMonth = Math.floor(end.getMonth() / 3) * 3;
    const start = new Date(end.getFullYear(), currentQuarterStartMonth - 3, 1);
    start.setHours(0, 0, 0, 0);
    const quarterEnd = new Date(end.getFullYear(), currentQuarterStartMonth, 0);
    quarterEnd.setHours(23, 59, 59, 999);
    return { start, end: quarterEnd };
  }

  if (lookback === LOOKBACK_YTD) {
    const start = new Date(end.getFullYear(), 0, 1);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }

  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

function normalizeKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function normalizeMonth(value) {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw.slice(0, 7);
  }

  if (/^\d{4}-\d{2}$/.test(raw)) {
    return raw;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const parts = raw.split('/');
    return `${parts[2]}-${parts[1]}`;
  }

  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}`;
}

function toDivisionName(value) {
  const text = String(value ?? '').trim();
  return text || UNASSIGNED_DIVISION;
}

function toGroupName(value) {
  const text = String(value ?? '').trim();
  return text || 'Unassigned Group';
}

function toCategoryName(value) {
  const text = String(value ?? '').trim();
  return text || 'Unclassified';
}

function toSafeNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildCategoryDivisionLookup(transactions) {
  const lookup = new Map();

  (transactions || []).forEach((tx) => {
    const category = String(tx?.manual_category ?? tx?.classification ?? '').trim();
    const division = toDivisionName(tx?.division);
    if (!category || !division) {
      return;
    }

    const key = normalizeKey(category);
    if (!lookup.has(key)) {
      lookup.set(key, division);
    }
  });

  return lookup;
}

function buildCategoryHierarchyLookup(transactions) {
  const lookup = new Map();

  (transactions || []).forEach((tx) => {
    const category = String(tx?.manual_category ?? tx?.classification ?? '').trim();
    if (!category) {
      return;
    }

    const key = normalizeKey(category);
    if (!lookup.has(key)) {
      lookup.set(key, {
        division: toDivisionName(tx?.division),
        group: String(tx?.group_name ?? '').trim() || 'Unassigned Group'
      });
    }
  });

  return lookup;
}

function toCurrency(amount) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2
  }).format(toSafeNumber(amount));
}

function RankingTable({
  title,
  subtitle,
  rows,
  columns,
  emptyText,
  onRowClick,
  selectedRowKey,
  expandedRowKey,
  expandedContentRenderer,
  firstColumnPaddingLeft = 0,
  rowKeyField = 'division'
}) {
  return (
    <Box sx={{ border: '1px solid #D4B896', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#FBF5EC' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #D4B896', backgroundColor: '#F0E3D0' }}>
        <Typography sx={{ color: '#2B1810', fontWeight: 700 }}>{title}</Typography>
        <Typography sx={{ color: '#7A5035', fontSize: '0.85rem' }}>{subtitle}</Typography>
      </Box>

      {rows.length === 0 ? (
        <Box sx={{ p: 2, color: '#7A5035' }}>{emptyText}</Box>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ minWidth: 900, tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    sx={{
                      color: '#2B1810',
                      backgroundColor: '#F0E3D0',
                      borderColor: '#D4B896',
                      fontWeight: 700,
                      width: column.width,
                      minWidth: column.width,
                      maxWidth: column.width,
                      textAlign: column.align || 'left',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      ...(column.sx || {})
                    }}
                  >
                    {column.key === columns[1]?.key ? (
                      <Box sx={{ pl: `${firstColumnPaddingLeft * 12}px`, display: 'inline-block' }}>{column.label}</Box>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <React.Fragment key={`${String(row?.[rowKeyField] ?? row?.division ?? index)}-${index}`}>
                  {(() => {
                    const currentRowKey = String(row?.[rowKeyField] ?? row?.division ?? index);

                    return (
                  <TableRow
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{
                      '& td': { borderColor: '#D4B896', color: '#2B1810' },
                      cursor: onRowClick ? 'pointer' : 'default',
                      backgroundColor: selectedRowKey && selectedRowKey === currentRowKey ? 'rgba(244,167,185,0.2)' : 'inherit',
                      '&:hover': onRowClick ? { backgroundColor: '#F0E3D0' } : undefined
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{
                          width: column.width,
                          minWidth: column.width,
                          maxWidth: column.width,
                          textAlign: column.align || 'left',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          ...(column.sx || {})
                        }}
                      >
                        {column.key === columns[1]?.key ? (
                          <Box sx={{ pl: `${firstColumnPaddingLeft * 12}px`, display: 'inline-block' }}>
                            {column.render ? column.render(row, index) : row[column.key]}
                          </Box>
                        ) : (
                          column.render ? column.render(row, index) : row[column.key]
                        )}
                      </TableCell>
                    ))}
                  </TableRow>

                    );
                  })()}

                  {expandedRowKey && expandedRowKey === String(row?.[rowKeyField] ?? row?.division ?? index) && expandedContentRenderer && (
                    <TableRow>
                      <TableCell colSpan={columns.length} sx={{ p: 0, borderColor: '#D4B896', backgroundColor: '#FBF5EC' }}>
                        {expandedContentRenderer(row)}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default function ReportingPage() {
  const [transactions, setTransactions] = useState([]);
  const [budgetMatrix, setBudgetMatrix] = useState({ months: [], categories: [], values: {} });
  const [lookbackWindow, setLookbackWindow] = useState(LOOKBACK_30_DAYS);
  const [selectedDeviationDivision, setSelectedDeviationDivision] = useState('');
  const [selectedDeviationGroup, setSelectedDeviationGroup] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [txResponse, budgetResponse] = await Promise.all([
        fetchWithDemoFallback(`${API_BASE}/api/transactions`, {
          method: 'GET',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' }
        }),
        fetchWithDemoFallback(`${API_BASE}/api/budgets`, {
          method: 'GET',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' }
        })
      ]);

      if (!txResponse.ok) {
        throw new Error(`Failed to fetch transactions (HTTP ${txResponse.status})`);
      }
      if (!budgetResponse.ok) {
        throw new Error(`Failed to fetch budgets (HTTP ${budgetResponse.status})`);
      }

      const [txData, budgetData] = await Promise.all([
        txResponse.json(),
        budgetResponse.json()
      ]);

      setTransactions(Array.isArray(txData) ? txData : []);
      setBudgetMatrix({
        months: Array.isArray(budgetData?.months) ? budgetData.months : [],
        categories: Array.isArray(budgetData?.categories) ? budgetData.categories : [],
        values: budgetData?.values && typeof budgetData.values === 'object' ? budgetData.values : {}
      });
      // divisionRows removed - we now derive hierarchy from transactions
    } catch (e) {
      setTransactions([]);
      setBudgetMatrix({ months: [], categories: [], values: {} });
      setError(e?.message || 'Unable to load reporting data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activeTransactions = useMemo(
    () => (transactions || []).filter((tx) => !(tx?.exclude === true || tx?.exclude === 1 || tx?.exclude === '1')),
    [transactions]
  );

  const datedActiveTransactions = useMemo(
    () => activeTransactions.map((tx) => ({ ...tx, _parsedDate: parseTransactionDate(tx?.transactiondate_iso ?? tx?.transactiondate) })),
    [activeTransactions]
  );

  const lookbackBounds = useMemo(() => buildLookbackBounds(lookbackWindow, new Date()), [lookbackWindow]);

  const filteredTransactions = useMemo(() => {
    const { start, end } = lookbackBounds;

    return datedActiveTransactions.filter((tx) => {
      if (!(tx._parsedDate instanceof Date) || Number.isNaN(tx._parsedDate.getTime())) {
        return false;
      }
      return tx._parsedDate >= start && tx._parsedDate <= end;
    });
  }, [datedActiveTransactions, lookbackBounds]);

  const filteredBudgetMonthSet = useMemo(() => {
    const startMonth = `${lookbackBounds.start.getFullYear()}-${String(lookbackBounds.start.getMonth() + 1).padStart(2, '0')}`;
    const endMonth = `${lookbackBounds.end.getFullYear()}-${String(lookbackBounds.end.getMonth() + 1).padStart(2, '0')}`;

    const availableBudgetMonths = new Set(budgetMatrix?.months || []);
    const budgetValues = (budgetMatrix?.values && typeof budgetMatrix.values === 'object') ? budgetMatrix.values : {};
    Object.values(budgetValues).forEach((categoryMonths) => {
      Object.keys(categoryMonths || {}).forEach((month) => {
        if (/^\d{4}-\d{2}$/.test(String(month))) {
          availableBudgetMonths.add(String(month));
        }
      });
    });

    return new Set(Array.from(availableBudgetMonths).filter((month) => month >= startMonth && month <= endMonth));
  }, [budgetMatrix, lookbackBounds]);

  const topSpentDivisions = useMemo(() => {
    const spendByDivision = new Map();

    filteredTransactions.forEach((tx) => {
      const amount = toSafeNumber(tx?.amount);
      if (amount >= 0) {
        return;
      }

      const division = toDivisionName(tx?.division);
      spendByDivision.set(division, (spendByDivision.get(division) || 0) + Math.abs(amount));
    });

    return Array.from(spendByDivision.entries())
      .map(([division, spend]) => ({ division, spend }))
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 5);
  }, [filteredTransactions]);

  const topDeviationDivisions = useMemo(() => {
    const categoryToDivision = buildCategoryDivisionLookup(filteredTransactions);
    const actualByDivisionByMonth = new Map();
    const budgetByDivisionByMonth = new Map();

    const addAmount = (store, division, month, value) => {
      if (!month) {
        return;
      }

      if (!store.has(division)) {
        store.set(division, new Map());
      }

      const monthStore = store.get(division);
      monthStore.set(month, (monthStore.get(month) || 0) + value);
    };

    filteredTransactions.forEach((tx) => {
      const amount = toSafeNumber(tx?.amount);
      if (amount >= 0) {
        return;
      }

      const month = normalizeMonth(tx?.transactiondate_iso ?? tx?.transactiondate);
      const division = toDivisionName(tx?.division);
      addAmount(actualByDivisionByMonth, division, month, Math.abs(amount));
    });

    (budgetMatrix?.categories || []).forEach((category) => {
      const key = normalizeKey(category);
      const division = categoryToDivision.get(key) || UNASSIGNED_DIVISION;

      (budgetMatrix?.months || []).forEach((month) => {
        if (!filteredBudgetMonthSet.has(month)) {
          return;
        }

        const budgetAmount = Math.abs(toSafeNumber(budgetMatrix?.values?.[category]?.[month]));
        if (!Number.isFinite(budgetAmount) || budgetAmount === 0) {
          return;
        }

        addAmount(budgetByDivisionByMonth, division, month, budgetAmount);
      });
    });

    const allDivisions = new Set([
      ...Array.from(actualByDivisionByMonth.keys()),
      ...Array.from(budgetByDivisionByMonth.keys())
    ]);

    const summary = Array.from(allDivisions)
      .map((division) => {
        const actualMonths = actualByDivisionByMonth.get(division) || new Map();
        const budgetMonths = budgetByDivisionByMonth.get(division) || new Map();

        const months = new Set([...Array.from(actualMonths.keys()), ...Array.from(budgetMonths.keys())]);

        let totalActual = 0;
        let totalBudget = 0;
        let absoluteDeviation = 0;

        months.forEach((month) => {
          const actual = actualMonths.get(month) || 0;
          const budget = budgetMonths.get(month) || 0;
          totalActual += actual;
          totalBudget += budget;
          absoluteDeviation += Math.abs(actual - budget);
        });

        return {
          division,
          absoluteDeviation,
          netDeviation: totalActual - totalBudget,
          totalActual,
          totalBudget
        };
      })
      .filter((row) => row.absoluteDeviation > 0)
      .sort((a, b) => b.netDeviation - a.netDeviation)
      .slice(0, 5);

    return summary;
  }, [filteredTransactions, budgetMatrix, filteredBudgetMonthSet]);

  useEffect(() => {
    if (!selectedDeviationDivision) {
      setSelectedDeviationGroup('');
      return;
    }

    const stillVisible = topDeviationDivisions.some((row) => row.division === selectedDeviationDivision);
    if (!stillVisible) {
      setSelectedDeviationDivision('');
      setSelectedDeviationGroup('');
    }
  }, [selectedDeviationDivision, topDeviationDivisions]);

  const selectedDivisionGroupRows = useMemo(() => {
    if (!selectedDeviationDivision) {
      return [];
    }

    const categoryHierarchy = buildCategoryHierarchyLookup(filteredTransactions);
    const actualByGroupByMonth = new Map();
    const budgetByGroupByMonth = new Map();

    const addAmount = (store, groupName, month, value) => {
      if (!groupName || !month) {
        return;
      }

      if (!store.has(groupName)) {
        store.set(groupName, new Map());
      }

      const monthStore = store.get(groupName);
      monthStore.set(month, (monthStore.get(month) || 0) + value);
    };

    filteredTransactions.forEach((tx) => {
      const amount = toSafeNumber(tx?.amount);
      if (amount >= 0) {
        return;
      }

      if (toDivisionName(tx?.division) !== selectedDeviationDivision) {
        return;
      }

      const month = normalizeMonth(tx?.transactiondate_iso ?? tx?.transactiondate);
      const groupName = toGroupName(tx?.group);
      addAmount(actualByGroupByMonth, groupName, month, Math.abs(amount));
    });

    (budgetMatrix?.categories || []).forEach((category) => {
      const key = normalizeKey(category);
      const mapping = categoryHierarchy.get(key);
      if (!mapping || mapping.division !== selectedDeviationDivision) {
        return;
      }

      const groupName = mapping.group;

      (budgetMatrix?.months || []).forEach((month) => {
        if (!filteredBudgetMonthSet.has(month)) {
          return;
        }

        const budgetAmount = Math.abs(toSafeNumber(budgetMatrix?.values?.[category]?.[month]));
        if (!Number.isFinite(budgetAmount) || budgetAmount === 0) {
          return;
        }

        addAmount(budgetByGroupByMonth, groupName, month, budgetAmount);
      });
    });

    const allGroups = new Set([
      ...Array.from(actualByGroupByMonth.keys()),
      ...Array.from(budgetByGroupByMonth.keys())
    ]);

    return Array.from(allGroups)
      .map((group) => {
        const actualMonths = actualByGroupByMonth.get(group) || new Map();
        const budgetMonths = budgetByGroupByMonth.get(group) || new Map();
        const months = new Set([...Array.from(actualMonths.keys()), ...Array.from(budgetMonths.keys())]);

        let totalActual = 0;
        let totalBudget = 0;

        months.forEach((month) => {
          totalActual += actualMonths.get(month) || 0;
          totalBudget += budgetMonths.get(month) || 0;
        });

        return {
          group,
          totalActual,
          totalBudget,
          netDeviation: totalActual - totalBudget
        };
      })
      .filter((row) => row.totalActual > 0 || row.totalBudget > 0)
      .sort((a, b) => b.netDeviation - a.netDeviation);
  }, [budgetMatrix, filteredBudgetMonthSet, filteredTransactions, selectedDeviationDivision]);

  useEffect(() => {
    if (!selectedDeviationGroup) {
      return;
    }

    const stillVisible = selectedDivisionGroupRows.some((row) => row.group === selectedDeviationGroup);
    if (!stillVisible) {
      setSelectedDeviationGroup('');
    }
  }, [selectedDeviationGroup, selectedDivisionGroupRows]);

  const selectedDivisionCategoryRows = useMemo(() => {
    if (!selectedDeviationDivision || !selectedDeviationGroup) {
      return [];
    }

    const categoryHierarchy = buildCategoryHierarchyLookup(filteredTransactions);
    const actualByCategoryByMonth = new Map();
    const budgetByCategoryByMonth = new Map();

    const addAmount = (store, categoryName, month, value) => {
      if (!categoryName || !month) {
        return;
      }

      if (!store.has(categoryName)) {
        store.set(categoryName, new Map());
      }

      const monthStore = store.get(categoryName);
      monthStore.set(month, (monthStore.get(month) || 0) + value);
    };

    filteredTransactions.forEach((tx) => {
      const amount = toSafeNumber(tx?.amount);
      if (amount >= 0) {
        return;
      }

      if (toDivisionName(tx?.division) !== selectedDeviationDivision) {
        return;
      }

      if (toGroupName(tx?.group) !== selectedDeviationGroup) {
        return;
      }

      const month = normalizeMonth(tx?.transactiondate_iso ?? tx?.transactiondate);
      const categoryName = toCategoryName(tx?.final_category ?? tx?.manual_category ?? tx?.classification);
      addAmount(actualByCategoryByMonth, categoryName, month, Math.abs(amount));
    });

    (budgetMatrix?.categories || []).forEach((category) => {
      const key = normalizeKey(category);
      const mapping = categoryHierarchy.get(key);
      if (!mapping || mapping.division !== selectedDeviationDivision || mapping.group !== selectedDeviationGroup) {
        return;
      }

      const categoryName = String(category).trim() || 'Unclassified';

      (budgetMatrix?.months || []).forEach((month) => {
        if (!filteredBudgetMonthSet.has(month)) {
          return;
        }

        const budgetAmount = Math.abs(toSafeNumber(budgetMatrix?.values?.[category]?.[month]));
        if (!Number.isFinite(budgetAmount) || budgetAmount === 0) {
          return;
        }

        addAmount(budgetByCategoryByMonth, categoryName, month, budgetAmount);
      });
    });

    const allCategories = new Set([
      ...Array.from(actualByCategoryByMonth.keys()),
      ...Array.from(budgetByCategoryByMonth.keys())
    ]);

    return Array.from(allCategories)
      .map((category) => {
        const actualMonths = actualByCategoryByMonth.get(category) || new Map();
        const budgetMonths = budgetByCategoryByMonth.get(category) || new Map();
        const months = new Set([...Array.from(actualMonths.keys()), ...Array.from(budgetMonths.keys())]);

        let totalActual = 0;
        let totalBudget = 0;

        months.forEach((month) => {
          totalActual += actualMonths.get(month) || 0;
          totalBudget += budgetMonths.get(month) || 0;
        });

        return {
          category,
          totalActual,
          totalBudget,
          netDeviation: totalActual - totalBudget
        };
      })
      .filter((row) => row.totalActual > 0 || row.totalBudget > 0)
      .sort((a, b) => b.netDeviation - a.netDeviation);
  }, [budgetMatrix, filteredBudgetMonthSet, filteredTransactions, selectedDeviationDivision, selectedDeviationGroup]);

  const spendColumns = [
    { key: 'rank', label: '#', width: RANK_COLUMN_WIDTH, align: 'center' },
    { key: 'division', label: 'Division', width: LABEL_COLUMN_WIDTH },
    { key: 'spend', label: 'Total Spent', width: AMOUNT_COLUMN_WIDTH, align: 'right', render: (row) => toCurrency(row.spend) }
  ];

  const deviationColumns = [
    { key: 'rank', label: '#', width: RANK_COLUMN_WIDTH, align: 'center' },
    { key: 'division', label: 'Division', width: LABEL_COLUMN_WIDTH },
    { key: 'totalActual', label: 'Spent', width: AMOUNT_COLUMN_WIDTH, align: 'right', render: (row) => toCurrency(row.totalActual) },
    { key: 'totalBudget', label: 'Budget', width: AMOUNT_COLUMN_WIDTH, align: 'right', render: (row) => toCurrency(row.totalBudget) },
    {
      key: 'netDeviation',
      label: 'Net (Spent - Budget)',
      width: NET_COLUMN_WIDTH,
      align: 'right',
      render: (row) => {
        const value = toSafeNumber(row.netDeviation);
        const prefix = value > 0 ? '+' : '';
        return `${prefix}${toCurrency(value)}`;
      }
    }
  ];

  return (
    <Box
      width="100vw"
      minHeight="100vh"
      padding="20px"
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      sx={{ backgroundColor: '#FFFFFF', color: '#2B1810', overflowY: 'auto', overflowX: 'hidden' }}
    >
      <Box sx={{ width: '100%', maxWidth: 1200, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <ASTITitle text="Reporting" />
            <ASTIText text="Quick ranking of highest spend divisions and the largest budget deviations." />
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="reporting-lookback-label" sx={{ color: '#2B1810' }}>Lookback Window</InputLabel>
              <Select
                labelId="reporting-lookback-label"
                value={lookbackWindow}
                label="Lookback Window"
                onChange={(event) => setLookbackWindow(event.target.value)}
                sx={{
                  color: '#2B1810',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4B896' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C4A882' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F4A7B9' },
                  '& .MuiSvgIcon-root': { color: '#2B1810' }
                }}
              >
                <MenuItem value={LOOKBACK_30_DAYS}>Last 30 days</MenuItem>
                <MenuItem value={LOOKBACK_LAST_MONTH}>Last month</MenuItem>
                <MenuItem value={LOOKBACK_LAST_QUARTER}>Last quarter</MenuItem>
                <MenuItem value={LOOKBACK_YTD}>Year to date</MenuItem>
              </Select>
            </FormControl>

            <Button variant="contained" onClick={fetchData} sx={{ backgroundColor: '#C47A30' }} disabled={loading}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        {loading && (
          <Box display="flex" alignItems="center" gap={2} sx={{ color: '#2B1810', mt: 1 }}>
            <CircularProgress size={20} />
            Loading reporting data...
          </Box>
        )}

        {!loading && !error && (
          <>
            <RankingTable
              title="Top 5 divisions by spend"
              subtitle="Based on non-excluded expense transactions in the selected lookback window."
              rows={topSpentDivisions}
              columns={spendColumns.map((column) => ({
                ...column,
                render: column.key === 'rank' ? (_, index) => index + 1 : column.render
              }))}
              emptyText="No expense transactions available yet."
            />

            <RankingTable
              title="Top 5 divisions by budget deviation"
              subtitle="Deviation is the summed absolute monthly gap between actual spend and division-assigned budgets in the selected window."
              rows={topDeviationDivisions}
              columns={deviationColumns.map((column) => ({
                ...column,
                render: column.key === 'rank' ? (_, index) => index + 1 : column.render
              }))}
              onRowClick={(row) => setSelectedDeviationDivision((prev) => (prev === row.division ? '' : row.division))}
              selectedRowKey={selectedDeviationDivision}
              expandedRowKey={selectedDeviationDivision}
              expandedContentRenderer={(divisionRow) => (
                <Box sx={{ backgroundColor: '#FBF5EC', borderTop: '1px solid #D4B896' }}>
                  <RankingTable
                    title={`Groups for ${divisionRow.division}`}
                    subtitle="Click a group to drill down to categories."
                    rows={selectedDivisionGroupRows}
                    columns={[
                      { key: 'rank', label: '#', width: RANK_COLUMN_WIDTH, align: 'center' },
                      { key: 'group', label: 'Group', width: LABEL_COLUMN_WIDTH },
                      { key: 'totalActual', label: 'Spent', width: AMOUNT_COLUMN_WIDTH, align: 'right', render: (row) => toCurrency(row.totalActual) },
                      { key: 'totalBudget', label: 'Budget', width: AMOUNT_COLUMN_WIDTH, align: 'right', render: (row) => toCurrency(row.totalBudget) },
                      {
                        key: 'netDeviation',
                        label: 'Net (Spent - Budget)',
                        width: NET_COLUMN_WIDTH,
                        align: 'right',
                        render: (row) => {
                          const value = toSafeNumber(row.netDeviation);
                          const prefix = value > 0 ? '+' : '';
                          return `${prefix}${toCurrency(value)}`;
                        }
                      }
                    ].map((column) => ({
                      ...column,
                      render: column.key === 'rank' ? (_, index) => index + 1 : column.render
                    }))}
                      firstColumnPaddingLeft={GROUP_INDENT_LEVEL}
                    rowKeyField="group"
                    onRowClick={(row) => setSelectedDeviationGroup((prev) => (prev === row.group ? '' : row.group))}
                    selectedRowKey={selectedDeviationGroup}
                    expandedRowKey={selectedDeviationGroup}
                    expandedContentRenderer={(groupRow) => (
                      <Box sx={{ backgroundColor: '#F0E3D0', borderTop: '1px solid #D4B896' }}>
                        <RankingTable
                          title={`Categories for ${groupRow.group}`}
                          subtitle={`Same math, now at category level within ${divisionRow.division}.`}
                          rows={selectedDivisionCategoryRows}
                          columns={[
                            { key: 'rank', label: '#', width: RANK_COLUMN_WIDTH, align: 'center' },
                            { key: 'category', label: 'Category', width: LABEL_COLUMN_WIDTH },
                            { key: 'totalActual', label: 'Spent', width: AMOUNT_COLUMN_WIDTH, align: 'right', render: (row) => toCurrency(row.totalActual) },
                            { key: 'totalBudget', label: 'Budget', width: AMOUNT_COLUMN_WIDTH, align: 'right', render: (row) => toCurrency(row.totalBudget) },
                            {
                              key: 'netDeviation',
                              label: 'Net (Spent - Budget)',
                              width: NET_COLUMN_WIDTH,
                              align: 'right',
                              render: (row) => {
                                const value = toSafeNumber(row.netDeviation);
                                const prefix = value > 0 ? '+' : '';
                                return `${prefix}${toCurrency(value)}`;
                              }
                            }
                          ].map((column) => ({
                            ...column,
                            render: column.key === 'rank' ? (_, index) => index + 1 : column.render
                          }))}
                          firstColumnPaddingLeft={CATEGORY_INDENT_LEVEL}
                          rowKeyField="category"
                          emptyText="No category-level spend/budget data available for the selected group."
                        />
                      </Box>
                    )}
                    emptyText="No group-level spend/budget data available for the selected division."
                  />
                </Box>
              )}
              emptyText="No comparable spend/budget data available yet."
            />
          </>
        )}
      </Box>
    </Box>
  );
}
